/* eslint-disable max-len */
import { detach, getParent, getParentOfType, isRoot, types, getRoot, } from 'mobx-state-tree';
import { intersection } from 'lodash-es';
import { v4 } from 'uuid';
import * as Yup from 'yup';
import { navStore } from './NavStore';
import { fieldValidator } from './YupAdapter';
// import { IRootModel } from 'RootModel';
import schema, { generateType, updateFieldsGenerator } from './Schema';
import { Item, ItemSection, getNewItem } from './ItemModel';
import { slugify } from './itemCommon';
const moveItem = (array, currentIndex, newIndex) => {
    array.splice(newIndex, 0, array.splice(currentIndex, 1)[0]);
};
export const ProgramItem = generateType(schema.getModel('programItem') /* , overrideMap */);
const ProgramSectionModel = types.model({
    id: types.identifier,
    name: types.string,
    sections: types.optional(types.array(types.late(() => ProgramSectionModel)), []),
    type: types.enumeration('SectionType', ['block', 'video', 'audio', 'quiz', 'article', 'pdf', 'download', 'container', 'course', 'knowledgeCheck', 'microcast', 'certification', 'typeform']),
    description: types.optional(types.string, ''),
    item: types.maybeNull(ProgramItem),
}).named('ProgramSectionModel')
    .views((self) => ({
    /**
     *
     * @returns the parent of this section - either the Program if its top level
     * or the section that contains this one.
     */
    isProgramSection() {
        return true;
    },
    getParent() {
        return getParent(self, 2);
    },
    canHaveSubSections() {
        return self.item === null;
    },
    canHaveItems() {
        return self.item == null;
        // section.sections == null || section.sections.length === 0;
    },
    canDelete() {
        return (!self.sections || self.sections.length === 0);
    },
}))
    .actions((self) => ({
    addChange() {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        getParentOfType(self, Program).addChange();
    },
    addSubSection(name) {
        const newSection = ProgramSectionModel.create({
            id: v4(),
            name,
            description: '',
            type: 'container',
        });
        self.sections.push(newSection);
        this.addChange();
        return newSection;
    },
    addSection(name, type) {
        const item = getNewItem(name, type);
        const newSection = ProgramSectionModel.create({
            id: v4(),
            name,
            type,
            description: '',
            item,
        });
        self.sections.push(newSection);
        this.addChange();
        return newSection;
    },
    moveSubSection(subSection, newIndex) {
        const currentIndex = self.sections.indexOf(subSection);
        const adjustedNewIndex = newIndex > currentIndex ? newIndex - 1 : newIndex;
        const tempArray = self.sections.slice();
        moveItem(tempArray, currentIndex, adjustedNewIndex);
        self.sections.replace(tempArray);
        this.addChange();
    },
    /**
     * Returns the subSection from the section and returns it.
     * Section is matched by ID.
     * @param subSection
     */
    removeSubSection(toRemove) {
        detach(toRemove);
        this.addChange();
    },
    /**
     * Adds a subsection into a section at the specified index.  If index is negative
     * it is added at the end.
     * @param subSection
     * @param atIndex
     */
    insertSubSection(subSection, atIndex) {
        if (atIndex < 0) {
            self.sections.push(subSection);
        }
        else {
            self.sections.splice(atIndex, 0, subSection);
        }
        this.addChange();
    },
    /**
     *
     * @param fields JSON, it is a flat list of key value pairs
     */
    updateFields(fields, formData, store) {
        if (fields.includes('item.course')) {
            const course = store.getOrLoad('course', formData.item.course);
            fields.push('name');
            formData.name = course.name;
        }
        if (fields.includes('item.microcast')) {
            const microcast = store.getOrLoad('microcast', formData.item.microcast);
            fields.push('name');
            formData.name = microcast.name;
        }
        updateFieldsGenerator(self)(fields, formData);
    },
}))
    .views((self) => ({
    isLesson() {
        return [
            'block',
            'video',
            'audio',
            'article',
            'pdf',
            'microcast',
            'scorm',
            'html',
            'typeform',
        ].includes(self.type);
    },
    isCourse() {
        return self.type === 'course';
    },
    isMicrocast() {
        return self.type === 'microcast';
    },
    isDownload() {
        return self.type === 'download';
    },
    calcLessonCount() {
        if (!self.sections) {
            return 0;
        }
        // count this section if its a lesson plus any lessons from sub-sections
        return self.sections.reduce((total, section) => total + section.calcLessonCount(), self.isLesson() ? 1 : 0);
    },
    calcCourseCount(includeMicrocasts = false) {
        if (!self.sections) {
            return 0;
        }
        // count this section if its a lesson plus any lessons from sub-sections
        return self.sections.reduce((total, section) => total + section.calcCourseCount(includeMicrocasts), (self.isCourse() || (includeMicrocasts && self.isMicrocast())) ? 1 : 0);
    },
    calcQuizCount() {
        if (!self.sections) {
            return 0;
        }
        // count this section if its a lesson plus any lessons from sub-sections
        return self.sections.reduce((total, section) => {
            if (section.isCourse() && section.item?.course) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                return total + section.item.course.calcQuizCount();
            }
            return total + section.calcQuizCount();
        }, 0);
    },
    isCertification() {
        return self.type === 'certification';
    },
    hasCertification() {
        if (!self.sections) {
            return 0;
        }
        // count this section if its a lesson plus any lessons from sub-sections
        return self.sections.reduce((total, section) => total + section.hasCertification(), self.isCertification() ? 1 : 0);
    },
    hasMaterials() {
        if (!self.sections) {
            return 0;
        }
        // count this section if its a lesson plus any lessons from sub-sections
        return self.sections.reduce((total, section) => total + section.hasMaterials(), self.isDownload() ? 1 : 0);
    },
    getFirstCourse() {
        if (self.type === 'course') {
            return self;
        }
        if (self.type === 'container' && self.sections.length) {
            // TODO - revisit this
            // eslint-disable-next-line no-restricted-syntax
            for (const section of self.sections) {
                const course = section.getFirstCourse();
                if (course) {
                    return course;
                }
            }
        }
        return null;
    },
}));
export const ProgramSection = types
    .compose(ItemSection, ProgramSectionModel)
    .named('ProgramSection');
const ProgramModel = generateType(schema.getModel('program') /* , overrideMap */)
    .views((self) => ({
    isAsset() {
        return false;
    },
    isCourse() {
        return false;
    },
    isDocument() {
        return false;
    },
    isProgram() {
        return true;
    },
    isMeeting() {
        return false;
    },
    isMicrocast() {
        return false;
    },
    itemImage() {
        return self.programImage;
    },
    isAncestor(child, possibleParent) {
        let parent = getParent(child);
        while (parent) {
            if (parent === possibleParent) {
                console.log('Is an ancestor', { child: child.name, parent: possibleParent.name });
                return true;
            }
            parent = isRoot(parent) ? null : getParent(parent);
        }
        // console.log('Not an ancestor', {child: child.name, parent: possibleParent.name});
        return false;
    },
}))
    .actions((self) => ({
    addSubSection(name) {
        const newSection = ProgramSection.create({
            id: v4(),
            name,
            type: 'container',
            description: '',
        });
        self.sections.push(newSection);
        self.addChange();
        return newSection;
    },
    addSection(name, type) {
        const item = getNewItem(name, type);
        const newSection = ProgramSection.create({
            id: v4(),
            name,
            type,
            description: '',
            item,
        });
        self.sections.push(newSection);
        self.addChange();
        return newSection;
    },
    moveSubSection(subSection, newIndex) {
        const currentIndex = self.sections.indexOf(subSection);
        const adjustedNewIndex = newIndex > currentIndex ? newIndex - 1 : newIndex;
        const tempArray = self.sections.slice();
        moveItem(tempArray, currentIndex, adjustedNewIndex);
        self.sections.replace(tempArray);
        self.addChange();
    },
    /**
     * Moves a section to a new location in the Program tree.
     *
     * @param sectionToMove
     * @param target
     * @param index
     */
    moveSection(sectionToMove, newParent, newIndex) {
        // if moving within the same list - need to adjust the index
        const currentParent = sectionToMove.getParent();
        if (currentParent.id === newParent.id) {
            currentParent.moveSubSection(sectionToMove, newIndex);
        }
        else {
            currentParent.removeSubSection(sectionToMove);
            newParent.insertSubSection(sectionToMove, newIndex);
        }
    },
    /**
     * Returns the subSection from the section and returns it.
     * Section is matched by ID.
     * @param subSection
     */
    removeSubSection(toRemove) {
        detach(toRemove);
        self.addChange();
    },
    insertSubSection(subSection, atIndex) {
        if (atIndex < 0) {
            self.sections.push(subSection);
        }
        else {
            self.sections.splice(atIndex, 0, subSection);
        }
        self.addChange();
    },
    updateFields(fields, formData) {
        updateFieldsGenerator(self)(fields, formData);
        self.updateInstuctorNames();
    },
    updateInstuctorNames() {
        let searchInstructors = '';
        if (self.instructors && self.instructors.length) {
            self.instructors.forEach((instructor) => {
                searchInstructors += `${instructor.name},`;
            });
        }
        self.searchInstructors = searchInstructors;
    },
    startProgram: async () => {
        const rootModel = getRoot(self); // as IRootModel;
        return rootModel.startProgram(self.key);
    },
}))
    .views((self) => ({
    calcLessonCount() {
        return self.sections.reduce((total, section) => total + section.calcLessonCount(), 0);
    },
    calcCourseCount(includeMicrocasts = false) {
        return self.sections.reduce((total, section) => total + section.calcCourseCount(includeMicrocasts), 0);
    },
    calcQuizCount() {
        return self.sections.reduce((total, section) => total + section.calcQuizCount(), 0);
    },
    hasCertification() {
        return self.sections.reduce((total, section) => total + section.hasCertification(), 0);
    },
    hasMaterials() {
        return self.sections.reduce((total, section) => total + section.hasMaterials(), 0);
    },
    /**
     * Returns true if the newParent is allowed to receive
     * the sectionToMove. Called by DND on CourseOutline
     * @param sectionToMove R
     * @param newParent
     * @param newIndex
     */
    canReceive(sectionToMove, newParent, newIndex) {
        // if moving within the same list - need to adjust the index
        const currentParent = sectionToMove.getParent();
        const currentIndex = currentParent.sections.indexOf(sectionToMove);
        // if a section has an item, into cant receive items or subsections
        if (!newParent.canHaveItems()) {
            return false;
        }
        // already here
        if (currentParent.id === newParent.id
            && (newIndex === currentIndex || newIndex === currentIndex + 1)) {
            return false;
        }
        // cant move to yourself
        if (sectionToMove === newParent) {
            return false;
        }
        // cant move a node to one if its children
        if (self.isAncestor(newParent, sectionToMove)) {
            return false;
        }
        return true;
    },
    getCourseToStart() {
        // TODO - this doenst look right?
        // eslint-disable-next-line no-restricted-syntax
        for (const section of self.sections) {
            const foundCourse = section.getFirstCourse();
            if (foundCourse) {
                return foundCourse;
            }
        }
        return null;
    },
    checkAccess() {
        // @TODO uses exact same logic as amplify/function/trackProgress.checkAccess
        // consolidate to one place.
        const rootModel = getRoot(self); // as IRootModel;
        const access = rootModel.currentAccess;
        if (!access) {
            return false;
        }
        if (access.purchasedPrograms?.indexOf(self.id) > 0) {
            console.log('Granted access to purchased program', self.id);
            return true;
        }
        const { excludeFromAllCourses, accessGroups: courseAccessGroups } = self;
        const customerAccessGroups = access.groups || [];
        if (!excludeFromAllCourses && customerAccessGroups.includes('ALL_COURSES')) {
            // console.log('Access granted via ALL_COURSES group');
            return true;
        }
        const commonGroups = intersection(courseAccessGroups, customerAccessGroups);
        if (commonGroups.length > 0) {
            console.log('Access granted via accessGroups: ', commonGroups);
            return true;
        }
        if (access.purchasedPrograms.indexOf(`,${self.id},`) >= 0) {
            console.log('Access granted via purchasedPrograms');
            return true;
        }
        return false;
    },
    /**
     *
     * @returns All the quizes from all the courses in this program
     */
    getQuizSections() {
        const courseSections = self.filterSections('course');
        const courseQuizSections = courseSections.map((courseSection) => (courseSection.item?.course.filterSections('quiz')));
        const programQuizSections = self.filterSections('quiz');
        return [...courseQuizSections.flat(), ...programQuizSections];
    },
}));
export const Program = types
    .compose(ProgramModel, Item).views(() => ({
    canHaveSubSections() {
        return true;
    },
}))
    .actions((self) => ({
    async preUpdate(isNew) {
        console.log('Program.preUpdate called', { self, isNew });
        self.setInstuctorNames();
        self.setCourseItems();
        self.setMicrocastItems();
        if (!self.manualSlug) {
            self.slug = slugify(self.name);
        }
        const slugValidator = fieldValidator('slug', {
            type: 'string',
            validation: 'slug',
        }, false);
        try {
            slugValidator?.validateSync(self.slug, { abortEarly: false });
        }
        catch (error) {
            if (error instanceof Yup.ValidationError) {
                console.error('Validation error:', error.errors);
                navStore.errorDialog.showError('Validation Error', `slug: ${error.message}`);
            }
            throw error;
        }
    },
}))
    .named('Program');
export const ProgramProgress = generateType(schema.getModel('programProgress'))
    .views((self) => ({
    getItem() {
        return self.program;
    },
}));
export const startProgramGQL = (isPortalApi) => (`
  mutation StartProgram(
    $programKey: String!
    $portalID: String!
    ${!isPortalApi ? '$organizationID: String!' : ''}
  ) {
    startProgram(
      programKey: $programKey
      portalID: $portalID
      ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    ) {
      statusCode
      courseProgress {
        ${schema.getGqlPropertyList('courseProgress', isPortalApi).join(' ')}
      }
      programProgress {
        ${schema.getGqlPropertyList('programProgress', isPortalApi).join(' ')}
      }
      message
    }
  }
`);
export const trackProgramProgressGQL = (isPortalApi) => (`
  mutation TrackProgramProgress(
    $programKey: String!
    $programItemID: String!
    $progress: String!
    ${!isPortalApi ? '$organizationID: String!' : ''}
  ) {
    trackProgramProgress(
      programKey: $programKey
      programItemID: $programItemID
      progress: $progress
      ${!isPortalApi ? 'organizationID: $organizationID' : ''}      
    ) {
      statusCode
      programProgress {
        ${schema.getGqlPropertyList('programProgress', isPortalApi).join(' ')}
      }
      message
    }
  }
`);
//# sourceMappingURL=ProgramModel.js.map